export type EvalInputs = {
  ticket?: number;
  marginPct?: number;
  daysPerMonth?: number;
  otherFixedCosts?: number;
  initialInvestment?: number;
};

export type EvalRubro = {
  key: string;
  label: string;
  pluralName: string;
  synonyms: string[];
  available: boolean;
  ticket: number;
  marginPct: number;
  daysPerMonth: number;
  otherFixedCosts: number;
};

export type EvalPrice = { priceCents: number; priceVariant: string; credits: number };

export type EvalConfig = {
  enabled: boolean;
  earlyAccess: boolean;
  disclaimer: string;
  compareDays: number;
  rubros?: EvalRubro[];
  price?: EvalPrice;
  testCheckout?: boolean;
  culqiPublicKey?: string;
};

export type EvalFactor = { key: string; label: string; detail: string; value: number; effect: 'up' | 'down' | 'neutral' | string };

export type EvalAssumptions = {
  ticket: number;
  margin: number;
  daysPerMonth: number;
  otherFixedCosts: number;
  initialInvestment?: number;
  rent: number;
  defaults: Record<string, boolean>;
};

export type EvalResult = {
  modelVersion: string;
  assumptions: EvalAssumptions;
  factors: EvalFactor[];
  multiplier: number;
  competitionRatio: number;
  clientsLow: number;
  clientsHigh: number;
  salesLow: number;
  salesHigh: number;
  fixedCostsTotal: number;
  profitLow: number;
  profitHigh: number;
  breakEvenClients: number;
  paybackApplicable: boolean;
  paybackLowMonths?: number;
  paybackHighMonths?: number;
  rentRatioLow: number;
  rentRatioHigh: number;
  rentRatioStatusLow: string;
  rentRatioStatusHigh: string;
  maxRent: number;
  maxRentViable: boolean;
  verdict: Verdict;
  downgraded?: boolean;
  mainReasonCode: string;
  mainReason: string;
  alerts: string[];
};

export type EvalPlace = { name: string; address?: string; lat: number; lng: number; rating?: number; reviews?: number; distanceM: number; source: string };
export type EvalGenerator = { key: string; label: string; count300: number; count500: number };

export type EvalSiteData = {
  signals: {
    competitors100: number;
    competitors300: number;
    competitors500: number;
    competitorsCapped?: boolean;
    competitorsInRadius: number;
    generators300: number;
    generators500: number;
    generatorsUnknown?: boolean;
    affluence: string;
    nse?: string;
  };
  competitors: EvalPlace[] | null;
  generators: EvalGenerator[] | null;
  affluenceStreet?: string;
  district?: string;
  nseEstimated?: boolean;
  sources: string[];
  warnings: string[];
  collectedAt: string;
};

export type Verdict = 'green' | 'amber' | 'red';

export type EvalSite = {
  id: string;
  position: number;
  status: 'processing' | 'ready' | 'failed' | string;
  error?: string;
  address: string;
  district?: string;
  lat: number;
  lng: number;
  listingUrl?: string;
  rent: number;
  areaM2?: number;
  verdict?: Verdict;
  mainReasonCode?: string;
  mainReason?: string;
  result?: EvalResult;
  data?: EvalSiteData;
};

export type Evaluation = {
  id: string;
  status: 'processing' | 'ready' | 'failed' | string;
  error?: string;
  createdAt: string;
  rubroKey: string;
  rubroLabel: string;
  rubroPlural: string;
  source?: string;
  modelVersion: string;
  verdict?: Verdict;
  mainReasonCode?: string;
  mainReason?: string;
  unlocked: boolean;
  unlockedAt?: string;
  price: EvalPrice;
  inputs: EvalInputs;
  sites: EvalSite[];
  canAddSite: boolean;
  compareUntil?: string;
  shareToken?: string;
  disclaimer: string;
  shared?: boolean;
};

export type EvaluationSummary = {
  id: string;
  createdAt: string;
  status: string;
  rubroLabel: string;
  address: string;
  verdict?: Verdict;
  unlocked: boolean;
  sites: number;
};

export const VERDICT_COPY: Record<Verdict, { title: string; phrase: string; color: string }> = {
  green: { title: 'Buena opción', phrase: 'Buena opción: incluso en un escenario bajo, cubrirías tus costos.', color: 'verde' },
  amber: { title: 'Con cuidado', phrase: 'Con cuidado: depende de que te vaya mejor que el escenario bajo.', color: 'ámbar' },
  red: { title: 'No te conviene así', phrase: 'No te conviene así: necesitarías más clientes de los que la zona sugiere. Renegocia el alquiler o mira otra opción.', color: 'rojo' },
};

export const EVAL_INCLUDES = [
  'Clientes al día, ventas y utilidad al mes (escenario bajo y alto)',
  'Cuántos clientes necesitas para no perder',
  'En cuántos meses recuperas tu inversión',
  'Alquiler como % de tus ventas y alquiler máximo para negociar',
  'Competencia a 100, 300 y 500 m con rating y reseñas',
  'Mapa con radios y quién atrae clientes a la zona',
  'Comparación con hasta 2 locales más durante 7 días',
  'Supuestos editables y PDF para tu socio o el banco',
];

const soles = new Intl.NumberFormat('es-PE', { maximumFractionDigits: 0 });

function roundTo(value: number, step: number, mode: 'floor' | 'ceil' | 'round' = 'round') {
  return Math[mode](value / step) * step;
}

function moneyStep(value: number) {
  const abs = Math.abs(value);
  if (abs >= 100000) return 1000;
  if (abs >= 10000) return 500;
  if (abs >= 1000) return 100;
  return 10;
}

export function formatSoles(value: number) {
  return `S/ ${soles.format(value)}`;
}

export function formatCredits(value: number) {
  return `${soles.format(value)} créditos`;
}

export function evalPriceLine(price?: EvalPrice | null) {
  const solesPart = formatSoles((price?.priceCents ?? 3900) / 100);
  const credits = price?.credits ?? 400;
  return `${solesPart} o ${formatCredits(credits)}`;
}

// Ranges are rounded outward so the shown band never looks more precise
// than the model is.
export function formatMoneyRange(low: number, high: number) {
  const lo = roundTo(low, moneyStep(low), 'floor');
  const hi = roundTo(high, moneyStep(high), 'ceil');
  if (lo === hi) return `alrededor de ${formatSoles(lo)}`;
  return `${formatSoles(lo)} a ${formatSoles(hi)}`;
}

export function formatProfitRange(low: number, high: number) {
  const r = (value: number) => formatSoles(roundTo(Math.abs(value), moneyStep(value), 'ceil'));
  if (high <= 0) return `pérdida de ${r(high)} a ${r(low)}`;
  if (low < 0) return `desde perder ${r(low)} hasta ganar ${r(high)}`;
  return formatMoneyRange(low, high);
}

export function formatClientsRange(low: number, high: number) {
  const lo = Math.max(0, Math.floor(low));
  const hi = Math.ceil(high);
  return `${lo} a ${hi}`;
}

export function formatPercentRange(low: number, high: number) {
  const a = Math.round(Math.min(low, high) * 100);
  const b = Math.round(Math.max(low, high) * 100);
  return a === b ? `${a}%` : `${a}% a ${b}%`;
}

export function formatMaxRent(value: number) {
  return formatSoles(roundTo(value, moneyStep(value), 'floor'));
}

export function paybackText(result: EvalResult) {
  if (!result.assumptions.initialInvestment) return '';
  const low = result.paybackLowMonths;
  const high = result.paybackHighMonths;
  if (high == null) return 'No se recupera en ninguno de los dos escenarios: en ambos el local pierde dinero.';
  const fast = Math.ceil(high);
  if (low == null) return `Solo se recupera en el escenario alto: en unos ${fast} meses. En el escenario bajo no se recupera.`;
  const slow = Math.ceil(low);
  return fast === slow ? `En unos ${fast} meses.` : `Entre ${fast} y ${slow} meses.`;
}

export const RENT_STATUS_LABEL: Record<string, string> = { healthy: 'saludable', alert: 'en alerta', high: 'alto' };

export function rentStatusTone(status: string) {
  return status === 'healthy' ? 'green' : status === 'alert' ? 'amber' : 'red';
}

export function evalErrorMessage(data: any, fallback: string) {
  if (data && typeof data.error === 'string' && data.error) return data.error;
  return fallback;
}
